import {
  decodeFiltersFromParams,
  encodeFiltersToParams,
  filtersEqual,
  PricingFilterUrlParams,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-url';
import {
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingFilterSelections,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { useEffect, useRef } from 'react';

interface UsePricingFiltersUrlSyncParams<T> {
  hydrate: (selections: PricingFilterSelections) => void;
  onUrlUpdate: (params: PricingFilterUrlParams) => void;
  routeParams: PricingFilterUrlParams;
  sections: PricingFilterSection<T>[];
  selections: PricingFilterSelections;
}

interface SyncSnapshot {
  routeParams: PricingFilterUrlParams;
  sectionShapes: Map<string, number | null>;
  selections: PricingFilterSelections;
}

const sectionShapes = <T>(
  sections: PricingFilterSection<T>[],
): Map<string, number | null> =>
  new Map(
    sections.map((section) => [
      section.id,
      section.kind === PricingFilterSectionKind.Pill
        ? section.options.length
        : null,
    ]),
  );

const hasNewlyAvailableSection = (
  previous: Map<string, number | null>,
  current: Map<string, number | null>,
): boolean => {
  for (const [id, optionCount] of current) {
    const previousCount = previous.get(id);
    if (previousCount === undefined) {
      return true;
    }
    if (previousCount === 0 && (optionCount ?? 0) > 0) {
      return true;
    }
  }
  return false;
};

export const usePricingFiltersUrlSync = <T>({
  hydrate,
  onUrlUpdate,
  routeParams,
  sections,
  selections,
}: UsePricingFiltersUrlSyncParams<T>): void => {
  const previousRef = useRef<SyncSnapshot | null>(null);

  useEffect(() => {
    const previous = previousRef.current;
    const shapes = sectionShapes(sections);
    previousRef.current = { routeParams, sectionShapes: shapes, selections };

    const selectionsChanged =
      previous && !filtersEqual(selections, previous.selections, sections);

    const routeChanged =
      !previous ||
      hasNewlyAvailableSection(previous.sectionShapes, shapes) ||
      !filtersEqual(
        decodeFiltersFromParams(routeParams, sections),
        decodeFiltersFromParams(previous.routeParams, sections),
        sections,
      );

    if (selectionsChanged) {
      if (
        !filtersEqual(
          decodeFiltersFromParams(routeParams, sections),
          selections,
          sections,
        )
      ) {
        onUrlUpdate(encodeFiltersToParams(selections, sections));
      }
      return;
    }

    if (!routeChanged) {
      return;
    }

    const decoded = decodeFiltersFromParams(routeParams, sections);
    if (!filtersEqual(decoded, selections, sections)) {
      hydrate(decoded);
    }
  }, [hydrate, onUrlUpdate, routeParams, sections, selections]);
};
